import { useCallback, useState } from "react";
import { ISensorAlertConfigLightning } from "stores/data/insightAlerts/insightAlertModel";
import {
    LIGHTNING_NOTIFICATION_WINDOW_SINGLE_MIN,
    LIGHTNING_RADIUS_DEFAULT_KM,
    LIGHTNING_SILENCE_WINDOW_DEFAULT_MIN,
    periodicityToNotificationWindowMin,
    LIGHTNING_PERIODICITY_DEFAULT,
    LIGHTNING_RECURRENCE_HOURS_DEFAULT,
} from "../lightning/lightning-constants";

/**
 * Local UI state hook for the lightning slice + anchor preservation logic.
 *
 * Spec 043 §FR-014a + §data-model "Anchor preservation rule":
 *   - On hydrate, capture `loadedAnchor` and `loadedPoint` from the GET response.
 *   - On submit, `computeAnchor()` returns:
 *       - `loadedAnchor` verbatim   when the user has NOT moved the point (lat/lng strictly equal)
 *       - `undefined`              when the user moved the point (so the BE persists `null`)
 *
 * The legacy frontend never introduces UI to set or visualize the anchor — it is
 * read-through metadata only.
 *
 * @returns slice + setters + clear/hydrate/computeAnchor
 */
export interface UseLightningSliceResult {
    /** Current slice or null when no lightning sub-block is configured. */
    slice: ISensorAlertConfigLightning | null;

    /** Place / replace the lightning monitoring point. Initializes the slice with defaults if absent. */
    setPoint: (point: { lat: number; lng: number }) => void;

    /** Update only the radius (slice must already exist). */
    setRadiusKm: (km: number) => void;

    /** Replace the slice entirely (e.g. from the post-selection form). */
    setSlice: (next: ISensorAlertConfigLightning) => void;

    /** Discard the slice (used by Popconfirm on the Raio tab X). */
    clear: () => void;

    /**
     * Hydrate from a GET response. Captures the original anchor + point so anchor
     * preservation can decide on save whether to forward `anchor` or `null`.
     */
    hydrate: (
        loaded: ISensorAlertConfigLightning | null | undefined,
    ) => void;

    /**
     * Returns the anchor to send on save. Preserves the loaded anchor verbatim when
     * the user has not moved the point; otherwise returns undefined (cleared).
     */
    computeAnchor: () => { stationId: number; regionId: number } | undefined;
}

const buildDefaultSlice = (point: {
    lat: number;
    lng: number;
}): ISensorAlertConfigLightning => ({
    point,
    radiusKm: LIGHTNING_RADIUS_DEFAULT_KM,
    allClearMin: LIGHTNING_SILENCE_WINDOW_DEFAULT_MIN,
    notificationWindowMin: periodicityToNotificationWindowMin(
        LIGHTNING_PERIODICITY_DEFAULT,
        LIGHTNING_RECURRENCE_HOURS_DEFAULT,
    ),
});

const _silenceUnused = LIGHTNING_NOTIFICATION_WINDOW_SINGLE_MIN; // exported by re-use elsewhere
void _silenceUnused;

export function useLightningSlice(): UseLightningSliceResult {
    const [slice, setSliceState] = useState<ISensorAlertConfigLightning | null>(
        null,
    );
    const [loadedAnchor, setLoadedAnchor] = useState<
        { stationId: number; regionId: number } | undefined
    >(undefined);
    const [loadedPoint, setLoadedPoint] = useState<
        { lat: number; lng: number } | undefined
    >(undefined);

    const setPoint = useCallback(
        (point: { lat: number; lng: number }) => {
            setSliceState((prev) =>
                prev ? { ...prev, point } : buildDefaultSlice(point),
            );
        },
        [],
    );

    const setRadiusKm = useCallback((km: number) => {
        setSliceState((prev) => (prev ? { ...prev, radiusKm: km } : prev));
    }, []);

    const setSlice = useCallback((next: ISensorAlertConfigLightning) => {
        setSliceState(next);
    }, []);

    const clear = useCallback(() => {
        setSliceState(null);
        // loadedAnchor / loadedPoint kept — if user re-creates a slice with the same point
        // we still don't want to leak the prior anchor; however the FE never sets anchor
        // explicitly, so we are safe.
    }, []);

    const hydrate = useCallback(
        (loaded: ISensorAlertConfigLightning | null | undefined) => {
            if (!loaded) {
                setSliceState(null);
                setLoadedAnchor(undefined);
                setLoadedPoint(undefined);
                return;
            }
            setSliceState({ ...loaded });
            setLoadedAnchor(loaded.anchor);
            setLoadedPoint({ ...loaded.point });
        },
        [],
    );

    const computeAnchor = useCallback(():
        | { stationId: number; regionId: number }
        | undefined => {
        if (!slice || !slice.point || !loadedAnchor || !loadedPoint) {
            return undefined;
        }
        if (
            slice.point.lat === loadedPoint.lat &&
            slice.point.lng === loadedPoint.lng
        ) {
            return loadedAnchor;
        }
        return undefined;
    }, [slice, loadedAnchor, loadedPoint]);

    return {
        slice,
        setPoint,
        setRadiusKm,
        setSlice,
        clear,
        hydrate,
        computeAnchor,
    };
}
